import { usePutCancelOrder } from "@multicines/services";
import { useDeleteTicketsReservation } from "@multicines/services";
import { useMoviesStore } from "@multicines/stores";
import { CONSTANTS } from "@multicines/utils";
import { deleteShoppingCart } from "artisn/shopping-cart";
import ClockRedSVG from "images/clock-red.svg";
import { useRouter } from "next/router";
import React, { useEffect, useState } from "react";

import Styles from "./Timeout.styles";
import { TimeoutProps as Props } from "./Timeout.types";
import Button from "../Button/Button";
import usePurchaseEvents from "components/checkout/Checkout/hooks/usePurchaseEvents";
import { getAuth } from "config/artisn.config";
import { notify } from "config/bugsnag.config";
import useI18n from "hooks/useI18n";
import { useAuthStore } from "stores/auth/auth.store";
import { useCheckoutStore } from "stores/checkout/checkout.store";
import { useStoresStore } from "stores/stores/stores.store";

const { SHOPPING_CART_DEFAULT_NAME, ACCOUNT_ID } = CONSTANTS.ARTISN;

const Timeout: React.FC<Props> = props => {
  const { replace } = useRouter();
  const t = useI18n();
  const selectedMovie = useMoviesStore(store => store.selectedMovie);
  const setReservationExpiration = useMoviesStore(
    store => store.setReservationExpiration
  );
  const resetSeatsToAllocate = useMoviesStore(
    store => store.resetSeatsToAllocate
  );
  const setIsSessionExpired = useMoviesStore(
    store => store.setIsSessionExpired
  );
  const { logPurchaseFail } = usePurchaseEvents();
  const { externalId } = selectedMovie ?? {};
  const isAnonymous = useAuthStore(state => state.isAnonymous);
  const uid = useAuthStore(state => state.uid);
  const { mutateAsync: putCancelOrder, reset: resetPut } = usePutCancelOrder({
    uid,
    isAnonymous
  });
  const resetRejectedOrder = useCheckoutStore(
    state => state.resetRejectedOrder
  );
  const placedOrder = useCheckoutStore(state => state.placedOrder);
  const selectedStore = useStoresStore(store => store.selectedStore);
  const {
    mutateAsync: cancelReservation,
    isLoading,
    reset
  } = useDeleteTicketsReservation(selectedStore);
  const [disable, setDisable] = useState(false);

  const clearCartAndReservation = async () => {
    setDisable(true);
    try {
      const deletedShoppingCart = await deleteShoppingCart({
        shoppingCartName: SHOPPING_CART_DEFAULT_NAME,
        anonymous: isAnonymous!,
        accountId: ACCOUNT_ID,
        customerId: uid!
      });
      if (deletedShoppingCart && placedOrder) {
        logPurchaseFail("El tiempo de compra ha expirado", deletedShoppingCart);
        await putCancelOrder(placedOrder.id);
        resetPut();
      }
      await cancelReservation();
      reset();
      // Refresh the token
      await getAuth.currentUser?.getIdToken(true);
      resetSeatsToAllocate();
      setReservationExpiration(undefined);
      resetRejectedOrder();
    } catch (e) {
      notify(e, "clearCartAndReservation - Timeout");
      setDisable(false);
    }
  };

  const goToBillboardHandler = async () => {
    await clearCartAndReservation();
    replace("/billboard");
  };

  const backToMovieHandler = async () => {
    if (!externalId) return;
    await clearCartAndReservation();
    replace(`/movie/${externalId}`);
  };

  useEffect(() => {
    return () => setIsSessionExpired(false);
  }, [setIsSessionExpired]);

  return (
    <Styles className="Timeout">
      <div className="Timeout__icon">
        <ClockRedSVG />
      </div>
      <p className="Timeout__text Timeout__title">{t.timeout.title}</p>
      <p className="Timeout__text Timeout__description">
        {t.timeout.description}
      </p>
      <Button
        disabled={disable || isLoading}
        className="Timeout__button"
        onClick={goToBillboardHandler}
      >
        {t.timeout.goToBillboard}
      </Button>
      <Button
        disabled={disable || isLoading}
        className="Timeout__button Timeout__exit"
        onClick={backToMovieHandler}
      >
        {t.timeout.backToMovie}
      </Button>
    </Styles>
  );
};

export default Timeout;
